import { getCanonicalSlug } from "../../utils/slug.js";
import { normalizeContentRating, normalizeSeasonNumber } from "../../utils/showFields.js";
import { getPrimaryRating } from "../../utils/showRatings.js";
export const DEFAULT_OG_IMAGE = "https://premierelens.com/og-image.jpg";

export function generateShowSlug(show) {
  return getCanonicalSlug(show);
}

export function getShowField(show, field, fallback = "") {
  if (!show) {
    return fallback;
  }
  const map = {
    title: show.title || show.omdbData?.title || "Unknown Show",
    year: show.year || show.omdbData?.year || "",
    network: show.sourceName || show.networks?.[0] || show.omdbData?.network || "Unknown Network",
    plot: show.plotOverview || show.omdbData?.plot || show.summary || "",
    rating: getPrimaryRating(show),
    ratingCount: show.omdbData?.ratingCount ?? null,
    releaseDate: show.releaseDate || "",
    genres: show.genres || show.omdbData?.genres || [],
    seasonNumber: normalizeSeasonNumber(show.seasonNumber),
    contentRating: normalizeContentRating(show.contentRating) || "",
    episodeCount: show.episodeCount ?? null,
    tags: Array.isArray(show.tags) ? show.tags : [],
    trailers: Array.isArray(show.trailers) ? show.trailers : [],
    poster: show.poster || show.omdbData?.poster || "",
    originalTitle: show.originalTitle || "",
    alternateTitles: Array.isArray(show.alternateTitles) ? show.alternateTitles : [],
  };
  return map[field] ?? fallback;
}

export function getPosterUrl(show) {
  const poster = getShowField(show, "poster");
  if (!poster || !poster.startsWith("http")) {
    return null;
  }
  return /\.(jpg|jpeg|png|webp|avif)$/i.test(poster) ? poster : null;
}

export function getPosterThumbnailUrl(show) {
  const thumb = show?.posterMetadata?.thumbnail;
  if (!thumb || !thumb.startsWith("http")) return null;
  return /\.(jpg|jpeg|png|webp|avif)$/i.test(thumb) ? thumb : null;
}

export function formatDate(isoDate) {
  if (!isoDate) {
    return "";
  }
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleDateString("en-GB", { month: "long", day: "numeric", year: "numeric" });
}

export function determineStatus(show) {
  const releaseDate = getShowField(show, "releaseDate");
  if (!releaseDate) {
    return { statusText: "Status unknown", statusDetail: "" };
  }
  const today = new Date();
  const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const release = new Date(`${releaseDate}T00:00:00`);
  if (Number.isNaN(release.getTime())) {
    return { statusText: "Status unknown", statusDetail: "" };
  }
  if (release <= localToday) {
    return { statusText: "Premiered", statusDetail: formatDate(releaseDate) };
  }
  return { statusText: "Coming soon", statusDetail: "" };
}

export function findRelatedShows(show, allShows, limit = 4) {
  const showSlug = generateShowSlug(show);
  const showGenres = new Set(getShowField(show, "genres", []));
  const showNetwork = getShowField(show, "network");
  const showImdb = String(show?.omdbData?.imdbId || "").toLowerCase();
  const showTitle = String(show?.title || "").toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
  const showYear = show?.year ?? show?.omdbData?.year ?? null;

  return allShows
    .filter((candidate) => {
      if (generateShowSlug(candidate) === showSlug) return false;
      const candidateImdb = String(candidate?.omdbData?.imdbId || "").toLowerCase();
      if (showImdb && candidateImdb && showImdb === candidateImdb) return false;
      const candidateTitle = String(candidate?.title || "").toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
      const candidateYear = candidate?.year ?? candidate?.omdbData?.year ?? null;
      if (showTitle && candidateTitle && showYear && candidateYear && showTitle === candidateTitle && String(showYear) === String(candidateYear)) {
        return false;
      }
      return true;
    })
    .map((candidate) => {
      let score = 0;
      const candidateGenres = new Set(getShowField(candidate, "genres", []));
      for (const genre of showGenres) {
        if (candidateGenres.has(genre)) {
          score += 2;
        }
      }
      const candidateNetwork = getShowField(candidate, "network");
      if (showNetwork && candidateNetwork && showNetwork === candidateNetwork) {
        score += 1;
      }
      const rating = Number(getShowField(candidate, "rating"));
      if (!Number.isNaN(rating) && rating >= 8) {
        score += 1;
      }
      return { candidate, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.candidate);
}
